import React, { useEffect, useState } from 'react';
import { Rocket, Loader2, ShieldAlert } from 'lucide-react';
import { useStrategyLabStore } from '../../stores/strategyLabStore';
import type { Strategy, CreateDeploymentRequest } from '../../types/strategyLab';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { fmtUsd } from './format';

interface DeployStrategyDialogProps {
  strategy: Strategy | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful deploy (e.g. to jump to the Deployments tab). */
  onDeployed?: () => void;
}

const DEFAULT_BUDGET = 5000;
const LOSS_PCT = 0.02; // default daily-loss stop = 2% of budget

const DeployStrategyDialog: React.FC<DeployStrategyDialogProps> = ({
  strategy, open, onOpenChange, onDeployed,
}) => {
  const createDeployment = useStrategyLabStore((s) => s.createDeployment);
  const actionError = useStrategyLabStore((s) => s.actionError);
  const clearActionError = useStrategyLabStore((s) => s.clearActionError);

  const [budget, setBudget] = useState(DEFAULT_BUDGET);
  const [maxLoss, setMaxLoss] = useState(Math.round(DEFAULT_BUDGET * LOSS_PCT));
  const [lossTouched, setLossTouched] = useState(false);
  const [perTradeCap, setPerTradeCap] = useState<number | ''>('');
  const [deploying, setDeploying] = useState(false);

  // Reset the form each time the dialog opens for a strategy.
  useEffect(() => {
    if (open) {
      setBudget(DEFAULT_BUDGET);
      setMaxLoss(Math.round(DEFAULT_BUDGET * LOSS_PCT));
      setLossTouched(false);
      setPerTradeCap('');
      setDeploying(false);
      clearActionError();
    }
  }, [open, strategy?.id, clearActionError]);

  // Keep the loss stop pinned to 2% of budget until the user overrides it.
  useEffect(() => {
    if (!lossTouched) setMaxLoss(Math.round(budget * LOSS_PCT));
  }, [budget, lossTouched]);

  const valid =
    !!strategy &&
    budget > 0 &&
    maxLoss > 0 &&
    maxLoss <= budget &&
    (perTradeCap === '' || (perTradeCap > 0 && perTradeCap <= budget));

  const handleDeploy = async () => {
    if (!strategy || !valid) return;
    setDeploying(true);
    const body: CreateDeploymentRequest = {
      strategy_id: strategy.id,
      budget_usd: budget,
      max_daily_loss_usd: maxLoss,
      ...(perTradeCap === '' ? {} : { per_trade_cap_usd: Number(perTradeCap) }),
    };
    const result = await createDeployment(body);
    setDeploying(false);
    if (result) {
      onOpenChange(false);
      onDeployed?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Rocket className="w-5 h-5 text-primary" />
            Deploy strategy
          </DialogTitle>
          <DialogDescription>
            {strategy
              ? <>Deploy <span className="font-medium text-foreground">{strategy.name}</span> with a daily budget. It trades the isolated Lab paper account.</>
              : 'Select a strategy to deploy.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label className="text-xs">Daily budget — max concurrent capital ($)</Label>
            <Input
              type="number" min={0} step={500} value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">
              The most capital deployed at once. Per-trade sizing draws down from this and never exceeds it.
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Max daily loss — sticky stop ($)</Label>
            <Input
              type="number" min={0} step={50} value={maxLoss}
              onChange={(e) => { setLossTouched(true); setMaxLoss(Number(e.target.value)); }}
            />
            <p className="text-xs text-muted-foreground">
              At this realized+open loss the deployment halts for the day
              {budget > 0 && (
                <> (default {fmtUsd(Math.round(budget * LOSS_PCT))} = 2% of budget)</>
              )}.
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Per-trade cap ($) — optional</Label>
            <Input
              type="number" min={0} step={100} value={perTradeCap} placeholder="none"
              onChange={(e) => setPerTradeCap(e.target.value === '' ? '' : Number(e.target.value))}
            />
          </div>

          {maxLoss > budget && (
            <p className="text-sm text-rose-500 flex items-center gap-1.5">
              <ShieldAlert className="w-4 h-4" /> Loss stop can't exceed the budget.
            </p>
          )}
          {actionError && <p className="text-sm text-rose-500">{actionError}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleDeploy} disabled={!valid || deploying}>
            {deploying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            Deploy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeployStrategyDialog;
